import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '../../App/Components/Button/Button'
import { useAppDispatch } from '../../App/Hooks/reduxHooks'
import { Checkbox } from '../../Common/Checkbox'
import { getHrUsers } from '../../User/UsersHr/Store/Actions'

import Client from './Client/Client'
import { ConfirmPasswordInputField } from './components/ConfirmPasswordInputField/ConfirmPasswordInputField'
import { EmailInputField } from './components/EmailInputField/EmailInputField'
import { FirstNameInputField } from './components/FirstNameInputField/FirstNameInputField'
import { LastNameInputField } from './components/LastNameInputField/LastNameInputField'
import { PasswordInputField } from './components/PasswordInputField/PasswordInputField'
import {
  Backdrop,
  ButtonsWrapper,
  CloseButton,
  CreatePersonDialogWrapper,
  Label,
  LabelText,
  PersonHeader,
  PersonHeaderWrapper,
  SimpleLabel,
} from './style'

type Props = {
  setPersonCrud: (resetState: null) => void
}

export const CreatePerson: React.FC<Props> = ({ setPersonCrud }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  const [personFirstName, setPersonFirstName] = useState<string>('')
  const [personLastName, setPersonLastName] = useState<string>('')
  const [personEmail, setPersonEmail] = useState<string>('')
  const [personPassword, setPersonPassword] = useState<string>('')
  const [personPasswordConfirm, setPersonPasswordConfirm] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [emailWarning, setEmailWarning] = useState<string>('')
  const [passwordMatchError, setPasswordMatchError] = useState<string>('')
  const [passwordLengthError, setPasswordLengthError] = useState<string>('')
  const [hasToPunch, setHasToPunch] = useState<boolean>(false)

  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(null)
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null)

  useEffect(() => {
    window.addEventListener('resize', () => setWindowWidth(window.innerWidth))
    return () => {
      window.removeEventListener('resize', () =>
        setWindowWidth(window.innerWidth),
      )
    }
  }, [])

  useEffect(() => {
    setCurrentDate(moment())
    setSelectedDate(moment())
  }, [])

  const createNewPerson = () => {
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

    if (personEmail && !emailRegex.test(personEmail)) {
      setEmailError(`${t('Invalid email format')}`)
      return false
    }

    if (!emailError && !passwordMatchError) {
      Client.createPerson(
        personFirstName,
        personPassword,
        hasToPunch,
        personLastName,
        personEmail,
        selectedDate,
      ).then(response => {
        if (response) {
          dispatch(getHrUsers())
        }
      })
      setPersonCrud(null)
    }
  }

  const handleDateChange = (newDate: any) => {
    setSelectedDate(newDate)
  }

  return (
    <>
      <CreatePersonDialogWrapper>
        <PersonHeaderWrapper>
          <PersonHeader>{t('Create person')}</PersonHeader>
          <CloseButton icon={faTimes} onClick={() => setPersonCrud(null)} />
        </PersonHeaderWrapper>
        <form onSubmit={createNewPerson}>
          <FirstNameInputField setPersonFirstName={setPersonFirstName} />
          <LastNameInputField setPersonLastName={setPersonLastName} />
          <DatePicker
            defaultValue={currentDate}
            slotProps={{
              textField: {
                helperText: 'DD/MM/YYYY',
              },
            }}
            label={t('Start of contract')}
            onChange={newDate => handleDateChange(newDate)}
          />
          <EmailInputField
            setPersonEmail={setPersonEmail}
            setEmailWarning={setEmailWarning}
          />
          {emailError && (
            <Label color={'error'}>
              <LabelText>{emailError}</LabelText>
            </Label>
          )}
          {emailWarning && (
            <Label color={'info'}>
              <LabelText>{emailWarning}</LabelText>
            </Label>
          )}
          <PasswordInputField
            setPersonPassword={setPersonPassword}
            setPasswordLengthError={setPasswordLengthError}
          />
          {passwordLengthError && (
            <Label color={'warning'}>
              <LabelText>{passwordLengthError}</LabelText>
            </Label>
          )}
          <ConfirmPasswordInputField
            personPasswordConfirm={personPasswordConfirm}
            personPassword={personPassword}
            setPersonPasswordConfirm={setPersonPasswordConfirm}
            setPasswordMatchError={setPasswordMatchError}
          />
          {passwordMatchError && (
            <Label color={'error'}>
              <LabelText>{passwordMatchError}</LabelText>
            </Label>
          )}
          <Checkbox
            mb="24px"
            mt="16px"
            onChange={() => setHasToPunch(!hasToPunch)}
            checked={hasToPunch}>
            {t('Has to punch')}
          </Checkbox>
          <SimpleLabel>
            {t('Fields marked with * sign are required.')}
          </SimpleLabel>
          <ButtonsWrapper>
            <Button
              onClick={() => setPersonCrud(null)}
              color={'rgba(0, 0, 0, 0.3)'}>
              {t('Cancel')}
            </Button>
            <input className="button" type="submit" value={t('Save')} />
          </ButtonsWrapper>
        </form>
      </CreatePersonDialogWrapper>
      <Backdrop onClick={() => setPersonCrud(null)} />
    </>
  )
}
